import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { CachedQueryKeys } from "./cachedQueryKeys";
import { fetchDtcSiteProduct, getBrandclubProduct } from "../rest/product";
import { getCatalogV2ProductByRetailerSku } from "../graphql/catalogV2Product";
import { useBCRouting } from "@/providers/BCRoutingContextProvider";
import { useCurrentProductContext } from "@/hooks/useCurrentProductContext";
import getStacklineSkus from "@/utils/bcProductMetadataUtils";
import { useMemo } from "react";
import { BCQueryOptions } from "./types";
import { ProductRewardSearchResult } from "@brandclub/common-ui";
import { DtcSiteProduct } from "../types";

export const brandclubProductQueryOptions = (
  stacklineSku: string | undefined
) => {
  return queryOptions({
    queryKey: [CachedQueryKeys.DTCProduct, stacklineSku],
    queryFn: async ({ signal }) => {
      if (!stacklineSku) {
        throw new Error("Failed to fetch DTC product: missing stacklineSku");
      }
      return getBrandclubProduct(stacklineSku, signal);
    },
    enabled: !!stacklineSku,
  });
};

interface CurrentDtcProductQueryOptionsArgs {
  stacklineSku?: string;
  retailerSku?: string;
  storeKind?: string;
  isCatalogV2: boolean;
}

const fetchLegacyCurrentProduct = (
  stacklineSku: string | undefined,
  signal?: AbortSignal
): Promise<ProductRewardSearchResult> => {
  if (!stacklineSku) {
    throw new Error("Failed to fetch DTC product: missing stacklineSku");
  }
  return getBrandclubProduct(stacklineSku, signal);
};

const fetchCatalogV2CurrentProduct = async ({
  retailerSku,
  storeKind,
  signal,
}: {
  retailerSku?: string;
  storeKind?: string;
  signal?: AbortSignal;
}): Promise<ProductRewardSearchResult> => {
  if (!retailerSku) {
    throw new Error("Failed to fetch DTC product: missing retailerSku");
  }
  if (!storeKind) {
    throw new Error("Failed to fetch DTC product: missing storeKind");
  }
  return getCatalogV2ProductByRetailerSku(retailerSku, storeKind, signal);
};

export const currentDtcProductQueryOptions = ({
  stacklineSku,
  retailerSku,
  storeKind,
  isCatalogV2,
}: CurrentDtcProductQueryOptionsArgs) => {
  return queryOptions({
    queryKey: isCatalogV2
      ? [CachedQueryKeys.DTCProduct, "catalog-v2", storeKind, retailerSku]
      : [CachedQueryKeys.DTCProduct, stacklineSku],
    queryFn: async ({ signal }) => {
      if (isCatalogV2) {
        return fetchCatalogV2CurrentProduct({
          retailerSku,
          storeKind,
          signal,
        });
      }
      return fetchLegacyCurrentProduct(stacklineSku, signal);
    },
    enabled: isCatalogV2 ? !!retailerSku && !!storeKind : !!stacklineSku,
  });
};

export const dtcSiteProductQueryOptions = (productUrl: string) => {
  return queryOptions({
    queryKey: [CachedQueryKeys.SiteProduct, productUrl],
    queryFn: async ({ signal }) => {
      return fetchDtcSiteProduct(productUrl, signal);
    },
  });
};

export const useCurrentBrandclubProduct = <TSelect = ProductRewardSearchResult>(
  args?: BCQueryOptions<
    typeof currentDtcProductQueryOptions,
    ProductRewardSearchResult,
    TSelect
  >
) => {
  const currentProductContext = useCurrentProductContext();
  const { isCatalogV2, stacklineSku } = currentProductContext;
  const variantSkus = useMemo(
    () =>
      isCatalogV2
        ? []
        : getStacklineSkus().filter((sku) => sku !== stacklineSku),
    [stacklineSku, isCatalogV2]
  );
  // prefetch all variants of the current product
  useQueries({
    queries: variantSkus.map((sku) => ({
      ...brandclubProductQueryOptions(sku),
      notifyOnChangeProps: [],
    })),
  });
  const currentProductQueryOptions =
    currentDtcProductQueryOptions(currentProductContext);

  return useQuery({
    ...currentProductQueryOptions,
    ...args,
    enabled:
      currentProductQueryOptions.enabled === false
        ? false
        : args?.enabled ?? currentProductQueryOptions.enabled,
    select: args?.select,
  });
};

export const useDtcSiteProduct = <TSelect = DtcSiteProduct | null>(
  args?: BCQueryOptions<
    typeof dtcSiteProductQueryOptions,
    DtcSiteProduct | null,
    TSelect
  >
) => {
  const bcRouting = useBCRouting();
  // indexOf returns -1 off a product page, and substring(-1) is the WHOLE
  // pathname — so this used to fetch `<current page>/product.json` on every
  // non-product route (`/search/product.json`, preview URLs), 404 through the
  // storefront's route resolver, and retry forever. No `/products/` segment
  // means there is no DTC product here: the query stays disabled.
  const productsIndex = bcRouting.location.pathname.indexOf("/products/");
  const productUrl =
    productsIndex >= 0
      ? bcRouting.location.origin +
        bcRouting.location.pathname.substring(productsIndex)
      : null;
  return useQuery({
    ...dtcSiteProductQueryOptions(productUrl ?? ""),
    ...args,
    enabled: productUrl !== null && (args?.enabled ?? true),
    select: args?.select,
  });
};
